// blog-board.js
// Public runtime for the Blog Board (see assets/blog-board-editor.js).
//
// The furnishing of the built-in card parts — image, title, date — is pure CSS,
// emitted server-side from the `board_css` section setting, so it needs no JavaScript at all.
// This file only creates the DOM for *free* blocks (extra text and images added on top of a
// card), which cannot be expressed as CSS. Their geometry still comes from `board_css`, keyed
// on `[data-bb-free="<id>"]`, so both breakpoints stay in real media queries.

const DATA_SELECTOR = 'script[data-bb-data]';
const EDIT_PARAM = 'edit';

/**
 * Shopify's CDN resizes a file when the URL carries `width`. A free block's picture arrives as
 * whatever the author dropped in — full-resolution originals, 300 kB behind a 220px block — so ask
 * for the size actually shown instead.
 *
 * Rounded up a ladder rather than to the exact pixel: every distinct width is its own CDN render
 * and its own cache entry, and a board full of near-identical widths would defeat both.
 *
 * Duplicated verbatim in assets/blog-board-editor.js, which is deliberately dependency-free.
 * Keep the two in step.
 */
const WIDTH_LADDER = [200, 300, 400, 600, 800, 1200, 1600, 2000, 2600, 3200];

export function sizedSrc(src, cssWidth) {
  if (!src || !src.includes('cdn.shopify.com')) return src;
  if (/[?&]width=/.test(src)) return src; // the author asked for a size explicitly
  const dpr = Math.min(globalThis.devicePixelRatio || 1, 3);
  const width = WIDTH_LADDER.find((w) => w >= Math.ceil((cssWidth || 0) * dpr));
  if (!width) return src; // wanted more than the ladder offers: leave the original alone
  return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
}

/** The width a block's picture is actually painted at, in CSS pixels. */
export function shownWidth(block) {
  const mobile = globalThis.innerWidth < 750;
  const geo = (mobile ? block.mobile : block.desktop) ?? block.desktop ?? block.mobile ?? {};
  return (geo.w ?? 400) * ((geo.zoom ?? 100) / 100);
}

/**
 * Reads the board payload the section printed as a JSON script tag.
 * @returns {{ free?: Record<string, Array<object>>, text?: Record<string, object> } | null}
 */
export function readBoardData() {
  const tag = document.querySelector(DATA_SELECTOR);
  if (!tag) return null;
  try {
    return JSON.parse(tag.textContent);
  } catch {
    console.warn('[blog-board] Board data is not valid JSON — ignoring it.');
    return null;
  }
}

/**
 * Builds the DOM node for one free block. Geometry lives in board_css, so the node itself
 * carries only identity and content.
 * @param {object} block
 * @returns {HTMLElement | null}
 */
export function createFreeBlockNode(block) {
  if (!block?.id) return null;

  const kind = block.type === 'image' || block.type === 'video' ? block.type : 'text';

  const el = document.createElement('div');
  el.dataset.bbFree = block.id;
  el.className = `bb-free bb-free--${kind}`;
  // overflow:hidden matches what the editor puts on the same wrapper. Without it a zoomed picture
  // paints outside its frame here but not in the preview, and the two would disagree.
  el.style.cssText = 'position:absolute;box-sizing:border-box;overflow:hidden;';
  if (block.bg) el.style.background = block.bg;

  if (kind !== 'text') {
    if (!block.src) return null;
    const media = document.createElement(kind === 'video' ? 'video' : 'img');

    if (kind === 'video') {
      // Silent, looping, no chrome — the block is a moving picture, not a player. Browsers only
      // allow autoplay when the video is muted and inline, and Safari reads `muted` off the
      // attribute rather than the property, so it has to be set both ways.
      Object.assign(media, { autoplay: true, loop: true, muted: true, playsInline: true, controls: false });
      media.setAttribute('muted', '');
      media.setAttribute('playsinline', '');
    } else {
      media.alt = block.alt ?? '';
      media.loading = 'lazy';
    }

    // Video is served as uploaded — the width parameter is an image transform.
    media.src = kind === 'video' ? block.src : sizedSrc(block.src, shownWidth(block));
    // Set inline as well as in the section stylesheet: the {% stylesheet %} bundle is not
    // cache-busted, so a stale copy must not be able to leave the media unsized.
    media.style.cssText = `display:block;width:100%;height:100%;object-fit:${block.fit ?? 'cover'};`;
    el.appendChild(media);
  } else {
    // Authored in the editor's contenteditable overlay; reuses the theme's RTE paragraph class.
    el.classList.add('rte2-jd-paragraph');
    el.innerHTML = block.html ?? '';
  }

  return el;
}

/**
 * Parts whose words the board may rewrite, and the node inside the card that holds them. Kept in
 * step with PARTS in assets/blog-board-editor.js, which is where an author sets the override.
 */
const TEXT_TARGETS = { date: '.blog-post-details span' };

/**
 * Writes the overridden words into one card. Unlike the geometry this cannot be expressed as CSS
 * without faking the text through generated content, so it runs here — which does mean the
 * article's own date is what paints until this script has run.
 *
 * @param {HTMLElement} card - A `.blog-post-item` element.
 * @param {Record<string, object>} text
 */
function applyText(card, text) {
  const key = card.dataset.bbKey;
  for (const [part, selector] of Object.entries(TEXT_TARGETS)) {
    // A card's own override wins over the one set for every card — same order as the editor.
    const value = text[key]?.[part] ?? text['*']?.[part];
    if (typeof value !== 'string' || value === '') continue;
    const node = card.querySelector(selector);
    // The equality check is what keeps this idempotent: hydrate re-runs on every mutation, and
    // writing the same text back would be a mutation of its own.
    if (node && node.textContent !== value) node.textContent = value;
  }
}

/**
 * Inserts every free block belonging to `card` that is not already there.
 * Idempotent, so it is safe to re-run after the paginated list morphs new pages in.
 * @param {HTMLElement} card - A `.blog-post-item` element.
 * @param {Record<string, Array<object>>} free
 */
function applyFreeBlocks(card, free) {
  const key = card.dataset.bbKey;
  const blocks = [...(free['*'] ?? []), ...(free[key] ?? [])];
  if (!blocks.length) return;

  const host = card.querySelector('.blog-post-card');
  if (!host) return;
  host.style.position = 'relative'; // The containing block for the absolutely placed blocks.

  for (const block of blocks) {
    // `*` blocks repeat across cards, so scope the id to the card to keep it unique.
    const id = free[key]?.includes(block) ? block.id : `${block.id}--${key}`;
    if (host.querySelector(`[data-bb-free="${CSS.escape(id)}"]`)) continue;

    const node = createFreeBlockNode({ ...block, id });
    if (node) host.appendChild(node);
  }
}

/**
 * @param {HTMLElement} grid
 * @param {Record<string, Array<object>>} free
 * @param {Record<string, object>} text
 */
function hydrate(grid, free, text) {
  for (const card of grid.querySelectorAll('.blog-post-item[data-bb-key]')) {
    applyFreeBlocks(card, free);
    applyText(card, text);
  }
}

function boot() {
  const isEdit = new URLSearchParams(location.search).get(EDIT_PARAM) === '1';

  if (isEdit) {
    // Liquid has no access to the query string, so the section hands us the editor URL instead.
    const src = document.querySelector('script[data-bb-editor-src]')?.dataset.bbEditorSrc;
    if (src) import(src).catch((error) => console.error('[blog-board] Editor failed to load', error));
    return; // The editor drives its own rendering, including free blocks.
  }

  const grid = document.querySelector('[data-bb-board]');
  const data = readBoardData();
  if (!grid || !data) return;
  const free = data.free ?? {};
  const text = data.text ?? {};

  hydrate(grid, free, text);

  // `PaginatedList` morphs later pages in and can discard nodes it did not render, so keep
  // re-applying. `applyFreeBlocks` skips blocks that are already present, which stops the
  // observer from feeding itself.
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      hydrate(grid, free, text);
    });
  }).observe(grid, { childList: true, subtree: true });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
